import type { DatabaseFile } from '../types';
import type { MarkdownService } from '../services/MarkdownService';
import type { GitHubService } from '../services/GitHubService';
import { displayNameFromPath } from '../utils/markdownPaths';
import { withoutGitBusyOverlay } from '../utils/gitBusy';
import { Bm25Index } from './bm25';

export type SearchHitKind = 'page' | 'section' | 'item';

export type SearchHit = {
  id: string;
  path: string;
  pageName: string;
  kind: SearchHitKind;
  sectionIndex?: number;
  itemIndex?: number;
  title: string;
  snippet: string;
  score: number;
};

export type HitMeta = Omit<SearchHit, 'score'>;

function folderPathLabel(path: string): string {
  const parts = path.replace(/\.md$/i, '').split('/').filter(Boolean);
  if (parts.length <= 1) return '';
  parts.pop();
  return parts.join(' / ');
}

function snippetFromText(text: string, query: string, maxLen = 72): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (!flat) return '';
  const q = query.trim().toLowerCase();
  if (!q) return flat.length <= maxLen ? flat : `${flat.slice(0, maxLen)}…`;
  const idx = flat.toLowerCase().indexOf(q.split(/\s+/)[0] ?? q);
  if (idx < 0) return flat.length <= maxLen ? flat : `${flat.slice(0, maxLen)}…`;
  const start = Math.max(0, idx - 20);
  const slice = flat.slice(start, start + maxLen);
  const prefix = start > 0 ? '…' : '';
  const suffix = start + maxLen < flat.length ? '…' : '';
  return `${prefix}${slice}${suffix}`;
}

/** Index one parsed file into BM25 documents (page, sections, items). */
export function indexFile(index: Bm25Index<HitMeta>, file: DatabaseFile): void {
  const folder = folderPathLabel(file.path);
  const pageBoost = [file.name, file.path.replace(/^database\//, '').replace(/\.md$/i, ''), folder]
    .filter(Boolean)
    .join(' ');

  index.add(`page:${file.path}`, `${pageBoost} ${pageBoost}`, {
    id: `page:${file.path}`,
    path: file.path,
    pageName: file.name,
    kind: 'page',
    title: file.name,
    snippet: folder || file.path.replace(/^database\//, ''),
  });

  file.sections.forEach((section, si) => {
    const sectionText = [section.title, ...section.items.map(i => i.text), ...section.prose].join(' ');
    index.add(`section:${file.path}:${si}`, `${file.name} ${section.title} ${sectionText}`, {
      id: `section:${file.path}:${si}`,
      path: file.path,
      pageName: file.name,
      kind: 'section',
      sectionIndex: si,
      title: `${file.name} › ${section.title}`,
      snippet: section.items[0]?.text?.slice(0, 80) ?? section.title,
    });

    section.items.forEach((item, ii) => {
      const itemText = [item.text, item.date].filter(Boolean).join(' ');
      if (!itemText.trim()) return;
      index.add(`item:${file.path}:${si}:${ii}`, `${file.name} ${section.title} ${itemText}`, {
        id: `item:${file.path}:${si}:${ii}`,
        path: file.path,
        pageName: file.name,
        kind: 'item',
        sectionIndex: si,
        itemIndex: ii,
        title: `${section.title} › ${item.text.split('\n')[0]!.slice(0, 60)}`,
        snippet: item.text.replace(/\s+/g, ' ').trim().slice(0, 80),
      });
    });
  });
}

export async function buildContentSearchIndex(
  github: GitHubService,
  markdown: MarkdownService,
  paths: string[],
  onProgress?: (done: number, total: number) => void
): Promise<Bm25Index<HitMeta>> {
  const index = new Bm25Index<HitMeta>();
  const mdPaths = paths.filter(p => p.endsWith('.md'));
  const total = mdPaths.length;
  const batchSize = 6;

  for (let i = 0; i < mdPaths.length; i += batchSize) {
    const batch = mdPaths.slice(i, i + batchSize);
    await Promise.all(
      batch.map(async path => {
        try {
          const { content, sha } = await withoutGitBusyOverlay(() => github.readFile(path));
          const file = markdown.parse(path, content, sha);
          indexFile(index, file);
        } catch {
          /* skip unreadable blobs */
        }
      })
    );
    onProgress?.(Math.min(i + batch.length, total), total);
  }

  index.finalize();
  return index;
}

export function searchContent(
  index: Bm25Index<HitMeta>,
  query: string,
  limit = 25
): SearchHit[] {
  return index.search(query, limit).map(r => ({
    ...r.meta,
    score: r.score,
    snippet: snippetFromText(r.meta.snippet, query),
  }));
}
